import { useState } from "react";
import axios from "axios";
import { Briefcase, Mail, Send, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { IMG, contact, investorPoints } from "@/data/vomarcData";
import { Zip } from "./Zip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  firm: "",
  investor_type: "Angel",
  ticket_size: "",
  interest: "Seed / pre-seed",
  message: "",
};

export function InvestorSection() {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.post(`${API}/investor-requests`, form);
      setSaved(response.data);
      toast.success("Thank you, your investor enquiry is received. The founder will personally respond.");
      setForm(initial);
    } catch {
      toast.error("Could not send investor enquiry. Please verify the email and try again.");
    } finally {
      setSaving(false);
    }
  };

  const mailUrl = `mailto:${contact.email}?subject=${encodeURIComponent("VOMARC Investor Enquiry")}`;

  return (
    <section id="investors" className="section investor-section" data-testid="investor-section">
      <div className="investor-section-head reveal">
        <span className="eyebrow" data-testid="investor-eyebrow">For investors</span>
        <h2 data-testid="investor-title">Why back VOMARC now.</h2>
        <p data-testid="investor-tagline">India's first unified design and execution platform. Open the panel below to see the pitch and request a founder call.</p>
      </div>
      <Zip
        testid="zip-why-back-vomarc"
        teaser={null}
        buttonOpen="Open investor pitch + enquiry form"
        buttonClose="Hide investor panel"
        defaultOpen={false}
      >
        <div className="investor-grid" data-testid="investor-grid">
          <div className="investor-pitch reveal" data-testid="investor-pitch">
            <p data-testid="investor-copy">VOMARC is the first platform that unifies design, products, vendors and execution for home transformation in India. We're building two connected products. VOMARC Main is the customer-facing app. VOMARC FSM is the execution layer. Both share the same room intelligence and billing engine.</p>
            <div className="investor-stats" data-testid="investor-stats">
              {investorPoints.map((p) => (
                <div key={p.id} className="investor-stat" data-testid={`investor-stat-${p.id}`}>
                  <strong>{p.value}</strong>
                  <span>{p.label}</span>
                </div>
              ))}
            </div>
            <div className="investor-image" data-testid="investor-image">
              <img src={IMG.investor} alt="VOMARC investor pitch" />
              <span className="investor-image-tag">Founder-led • Pre-seed open</span>
            </div>
          </div>

          <form className="investor-form" onSubmit={submit} data-testid="investor-form">
          <div className="form-head">
            <Briefcase size={20} />
            <div>
              <strong>Investor enquiry</strong>
              <span>For angels, VCs and strategic partners</span>
            </div>
          </div>
          <Input required value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your name" data-testid="investor-form-name-input" />
          <Input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="Email" data-testid="investor-form-email-input" />
          <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="Phone (optional)" data-testid="investor-form-phone-input" />
          <Input value={form.firm} onChange={(e) => update("firm", e.target.value)} placeholder="Firm / fund (optional)" data-testid="investor-form-firm-input" />
          <div className="form-row">
            <select value={form.investor_type} onChange={(e) => update("investor_type", e.target.value)} data-testid="investor-form-type-select">
              <option>Angel</option>
              <option>VC fund</option>
              <option>Family office</option>
              <option>Strategic partner</option>
              <option>Accelerator</option>
            </select>
            <Input value={form.ticket_size} onChange={(e) => update("ticket_size", e.target.value)} placeholder="Ticket size (e.g. ₹50L)" data-testid="investor-form-ticket-input" />
          </div>
          <select value={form.interest} onChange={(e) => update("interest", e.target.value)} data-testid="investor-form-interest-select">
            <option>Seed / pre-seed</option>
            <option>Series A</option>
            <option>Strategic partnership</option>
            <option>Pilot collaboration</option>
            <option>Other</option>
          </select>
          <Textarea value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Why are you interested in VOMARC? Any specific questions for the founder?" data-testid="investor-form-message-textarea" />
          <Button type="submit" className="primary-cta full" disabled={saving} data-testid="investor-form-submit-button">
            <Send size={17} /> {saving ? "Sending..." : "Request founder call"}
          </Button>
          {saved && (
            <div className="thankyou-panel" data-testid="investor-form-success">
              <TrendingUp size={16} />
              <strong>Thank you, {saved.name}.</strong>
              <span>Your investor enquiry <em>{saved.id.slice(0, 8)}</em> is received. The founder will reach out within 48 hours with a personalised pitch deck and call invite.</span>
            </div>
          )}
          <a className="form-fallback" href={mailUrl} data-testid="investor-form-email-fallback"><Mail size={14} /> Or email {contact.email}</a>
        </form>
        </div>
      </Zip>
    </section>
  );
}
